import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import ApiConfig from '../../config/ApiConfig';
import Axios from 'axios';
import { Box, FormControl, Grid, MenuItem, Select, Typography } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';

function LineChart() {
  const [options, setOptions] = useState({
    chart: {
      height: 350,
      type: 'line',
      zoom: {
        enabled: false
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return Math.round(val);
        }
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      width: [5, 5],
      curve: 'straight',
      dashArray: [0, 0]
    },
    title: {
      align: 'left'
    },
    legend: {
      tooltipHoverFormatter: function (val, opts) {
        return val + ' - ' + opts.w.globals.series[opts.seriesIndex][opts.dataPointIndex] + ''
      }
    },
    markers: {
      size: 0,
      hover: {
        sizeOffset: 6
      }
    },
   xaxis: {
  categories: [],
  labels: {
    formatter: function (value, timestamp, opts) {
      if (opts?.w?.globals?.categoryLabels?.length > 0) {
        const categories = opts.w.globals.categoryLabels;
        if (value === categories[0] || value === categories[categories.length - 1]) {
          return value; // Show first and last date labels
        }
        return ''; // Hide intermediate labels
      }
      return value || '';
    }
  }
},
    tooltip: {
      y: [
        {
          title: {
            formatter: function (val) {
              return val + " QR Codes"
            }
          }
        },
        {
          title: {
            formatter: function (val) {
              return val + " Tests"
            }
          }
        }
      ]
    },
    grid: {
      borderColor: '#f1f1f1',
    },
  });

  const [data, setData] = useState([])
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [allDates, setAllDates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Convert DD-MM-YYYY to Date object
  const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split('-');
    return new Date(`${year}-${month}-${day}`);
  };

  // Convert Date object to DD-MM-YYYY format for API
  const formatDateToAPI = (date) => {
    if (!date) return null;
    const formattedDate = new Date(date);
    const day = String(formattedDate.getDate()).padStart(2, '0');
    const month = String(formattedDate.getMonth() + 1).padStart(2, '0');
    const year = formattedDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format date for display (DD/MM/YYYY)
  const formatDateForDisplay = (dateStr) => {
    const [day, month, year] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleDateChange = (date) => {
    setFromDate(date);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
  };

  const MulitData = async () => {
    try {
      setLoading(true);

      const formattedFrom = formatDateToAPI(fromDate);
      const formattedTo = formatDateToAPI(toDate);

      const res = await Axios({
        method: "GET",
        url: ApiConfig.multiData,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("id_token")}`,
        },
        params: {
          fromDate: formattedFrom,
          toDate: formattedTo,
        },
      });

      if (res.data.status === 200) {
        const inputData = res?.data?.data;

        // Get all unique dates from both data sets
        const allDatesSet = new Set();
        Object.keys(inputData.uploadedQRCodeCount || {}).forEach(date => allDatesSet.add(date));
        Object.keys(inputData.completedTestCount || {}).forEach(date => allDatesSet.add(date));

        // Convert to array and sort chronologically
        const sortedDates = Array.from(allDatesSet).sort((a, b) => {
          const dateA = parseApiDate(a);
          const dateB = parseApiDate(b);
          return dateA - dateB;
        });

        // Store all dates for reference
        setAllDates(sortedDates);

        // Set default dates if not already set
        if (!fromDate && sortedDates.length > 0) {
          const earliestDate = parseApiDate(sortedDates[0]);
          setFromDate(earliestDate);
        }

        if (!toDate && sortedDates.length > 0) {
          const latestDate = parseApiDate(sortedDates[sortedDates.length - 1]);
          setToDate(latestDate);

          // If fromDate is set but toDate is not, set toDate to latest date
          if (fromDate) {
            setToDate(latestDate);
          }
        }

        // Filter dates based on selected range
        let filteredDates = sortedDates;
        if (fromDate && toDate) {
          filteredDates = sortedDates.filter(dateStr => {
            const currentDate = parseApiDate(dateStr);
            const from = new Date(fromDate);
            const to = new Date(toDate);
            return currentDate >= from && currentDate <= to;
          });
        }

        // Format dates for display
        const formattedDates = filteredDates.map(dateStr => formatDateForDisplay(dateStr));

        // Create series data for each metric
        const seriesData = [
          {
            name: "Total Uploaded QR Code",
            data: filteredDates.map(date => inputData.uploadedQRCodeCount[date] || 0)
          },
          {
            name: "Total Completed Test",
            data: filteredDates.map(date => inputData.completedTestCount[date] || 0)
          }
        ];

      setOptions(prevOptions => ({
  ...prevOptions,
  xaxis: {
    ...prevOptions.xaxis,
    categories: formattedDates,
    labels: {
      formatter: function (value, timestamp, opts) {
        if (opts?.w?.globals?.categoryLabels?.length > 0) {
          const categories = opts.w.globals.categoryLabels;
          if (value === categories[0] || value === categories[categories.length - 1]) {
            return value; // Show first and last date labels
          }
          return ''; // Hide intermediate labels
        }
        return value || '';
      }
    }
  }
}));

        setData(seriesData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    MulitData();
  }, [fromDate, toDate]);

  // Get min and max dates for date picker constraints
  const getMinMaxDates = () => {
    if (allDates.length === 0) return { minDate: null, maxDate: null };

    const minDate = parseApiDate(allDates[0]);
    const maxDate = parseApiDate(allDates[allDates.length - 1]);

    return { minDate, maxDate };
  };

  const { minDate, maxDate } = getMinMaxDates();
  return (
    <div id="chart">
      <Grid container xs={12} justifyContent='flex-end' style={{ gap: '10px', marginTop: "-20px" }}>
        <Grid item xs={12} sm={6} md={6} lg={5}>
          <div >
            <KeyboardDatePicker

              // placeholder="DD/MM/YYYY"
              InputLabelProps={{ shrink: true }}
              placeholder="DD/MM/YYYY"
              format="DD/MM/YYYY"
              label='From Date'
              inputVariant="outlined"
              disableFuture
              margin="dense"
              name="dateOfBirth"
              value={fromDate}
              onChange={handleDateChange}
              KeyboardButtonProps={{
                style: { marginRight: '-15px' },
              }}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={5}>


          <div >
            <KeyboardDatePicker
              className={` textFeilds`}

              placeholder="DD/MM/YYYY"
              format="DD/MM/YYYY"
              label='To Date'
              InputLabelProps={{ shrink: true }}
              inputVariant="outlined"
              minDate={fromDate ? fromDate : new Date()}
              disableFuture
              margin="dense"
              name="dateOfBirth"
              value={toDate}
              onChange={handleToDateChange}
              KeyboardButtonProps={{
                style: { marginRight: '-15px' },
              }}

            />{" "}
          </div>
        </Grid>
        {/* <FormControl >
          <Select
            open={open1}
            onClose={handleClose1}
            onOpen={handleOpen1}
            value={usertype}
            onChange={(event) => {
              handleChange(event);
            }}
          >
            <MenuItem value="week">Weekly</MenuItem>
            <MenuItem value="month">Monthly</MenuItem>
            <MenuItem value="year">Yearly</MenuItem>
          </Select>
        </FormControl> */}
      </Grid>
      {data[0]?.data?.length == 0 && data[1]?.data?.length == 0 ? (<Box style={{ height: "350px", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}> <Typography variant="h5"
        color="textSecondary" style={{ display: "flex", justifyContent: "center", alignItems: "center", color: '#6E6E6E', fontFamily: 'poppins' }} >No Data Found</Typography> </Box>) : (
        <ReactApexChart options={options} series={data} type="line" height={350} />
      )}

      {/* <ReactApexChart options={options} series={data} type="line" height={350} /> */}



    </div>
  );
}

export default LineChart;
